import { createBrowserClient } from '@supabase/ssr';
import type { SupabaseClient } from '@supabase/supabase-js';

import {
  getBrowserAuthAudience,
  getBrowserSupabaseCookieOptions,
} from '@/utility/host-context-browser';
import {
  SUPABASE_STORAGE_KEYS,
  type AuthAudience,
  type SupabaseCookieOptions,
} from '@/utility/host-context';

import { getBrowserSupabaseConfig } from './config';

const browserClients = new Map<AuthAudience, SupabaseClient>();

function browserCookieOptions(audience: AuthAudience): SupabaseCookieOptions {
  return getBrowserSupabaseCookieOptions(audience) ?? {
    name: SUPABASE_STORAGE_KEYS[audience],
    path: '/',
    sameSite: 'lax',
    secure: false,
  };
}

/**
 * Creates a browser Supabase client for one isolated auth audience.
 *
 * @param audience Owner or consumer session family.
 * @returns A browser Supabase client using the audience storage key.
 */
export function createBrowserSupabaseClient(
  audience: AuthAudience = getBrowserAuthAudience(),
): SupabaseClient {
  const cachedClient = browserClients.get(audience);
  if (cachedClient)
    return cachedClient;

  const { url, anonKey } = getBrowserSupabaseConfig();
  const client = createBrowserClient(url, anonKey, {
    isSingleton: false,
    cookieOptions: browserCookieOptions(audience),
    auth: {
      autoRefreshToken: true,
      detectSessionInUrl: true,
      persistSession: true,
    },
  });
  browserClients.set(audience, client);
  return client;
}
